import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { TrafficRow } from '@netfoundry-ui/shared/model';
import type { ListColumn, ListTableOptions } from 'ziti-console-lib';
import type { CoverageExplain, CoverageRow, CoverageState, CoverageTreeNode } from './coverage-sankey.adapter';
import { CoverageStateCellComponent, type CoverageStateTone } from './coverage-state-cell.component';
import { CoverageIncludeCellComponent } from './coverage-include-cell.component';

/** Toggling a node's membership in the service, emitted up so the dialog can edit it. A parent carries all
 *  its items (cascade); a leaf carries one. */
export interface CoverageIncludeToggle {
    /** Source rows edit admission (allowed sources); endpoint rows edit the service's targets. */
    target: 'source' | 'endpoint';
    /** The items to edit: source labels (IP / `@identity`), or endpoint keys (`address:port`). */
    items: string[];
    /** True to include them in the service, false to exclude. */
    include: boolean;
}

/** A breakdown row as the embedded list-table reads it (flat endpoint rows, or a Source identity/address tree). */
interface CovTableRow {
    id: string;
    primary: string;
    circuits: number;
    service?: string;
    tone: CoverageStateTone;
    stateLabel: string;
    /** Whether every source under this row is currently included in (admitted by) the governing service. */
    included: boolean;
    /** A parent only some of whose sources are included (the checkbox shows a dash). */
    indeterminate: boolean;
    /** The admission targets under this row (for the include/exclude edit). */
    sources: string[];
    /** The flows this row aggregates, so hovering it can highlight its path in the coverage flow-map. */
    flows: TrafficRow[];
    children?: CovTableRow[];
}

/**
 * The body of the coverage detail drawer - the structured readout for a clicked node/ribbon. A summary
 * (meter + `Coverage NN%`, then the source-count tiles) sits above the breakdown, which is rendered with
 * the same `lib-list-table` the Traffic Analysis flow-detail panel uses. An endpoint click breaks down into
 * a Source identity -> Source address tree, each node showing its coverage State and a leading include
 * checkbox that includes/excludes that source (or, on a parent, its whole subtree) from the governing
 * service. Other clicks show a flat list of endpoints. Hovering a row highlights its path in the flow-map.
 */
@Component({
    selector: 'app-coverage-detail-content',
    templateUrl: './coverage-detail-content.component.html',
    styleUrls: ['./coverage-detail-content.component.scss'],
    standalone: false,
})
export class CoverageDetailContentComponent {
    @Input() set explain(value: CoverageExplain) {
        this._explain = value;
        this.rebuild();
    }
    get explain(): CoverageExplain {
        return this._explain;
    }

    /** A row is hovered: its flows, so the page can highlight that row's path in the flow-map. Null on leave. */
    @Output() rowHover = new EventEmitter<{ flows: TrafficRow[] } | null>();

    /** A node's include checkbox was toggled: the dialog edits the governing service and recomputes. */
    @Output() toggleInclude = new EventEmitter<CoverageIncludeToggle>();

    readonly tableId = 'coverage-drawer-breakdown';
    columns: ListColumn[] = [];
    childColumnsByLevel: ListColumn[][] = [];
    rowData: CovTableRow[] = [];
    isTree = false;

    private _explain!: CoverageExplain;

    // Read-only presentation except for the include checkboxes (a custom cell, not the lib's selection), no
    // row menu, kept within the panel width, and no toolbar/pagination chrome (the drawer needs none).
    // Held as a field (rebuilt only when the data changes) so the template binding does not hand the table a
    // fresh options object on every change-detection pass.
    tableOptions: ListTableOptions = this.buildTableOptions();

    private buildTableOptions(): ListTableOptions {
        return {
            tree: this.isTree,
            noSelect: true,
            noMenu: true,
            fitColumns: true,
            toolbar: {
                search: { show: false },
                filters: { show: false },
                refresh: { show: false },
                autoRefresh: { show: false },
                columns: { show: false },
                pageSize: { show: false },
                pagination: { show: false },
            },
        };
    }

    get coveredCircuits(): number {
        return this._explain?.coveredCircuits ?? 0;
    }
    get excludedCircuits(): number {
        return Math.max(0, (this._explain?.selectedCircuits ?? 0) - this.coveredCircuits);
    }
    /** Covered share of selected circuits, as a whole percent - the meter's headline value, stated once. */
    get coveragePercent(): number {
        const total = this._explain?.selectedCircuits ?? 0;
        return total ? Math.round((this.coveredCircuits / total) * 100) : 0;
    }
    get totalCircuits(): number {
        return this._explain?.selectedCircuits ?? 0;
    }
    get isSourceRows(): boolean {
        return this._explain?.rowsBy === 'source';
    }
    get hasService(): boolean {
        return (this._explain?.services?.length ?? 0) > 0;
    }
    get serviceLabel(): string {
        return this._explain?.services?.length ? this._explain.services.join(', ') : '';
    }
    /** Whether a service governs this endpoint (one to include/exclude sources from), named or not. */
    get governed(): boolean {
        return !!this._explain?.governed;
    }
    /** Whether any service exists to edit (an endpoint can be added back to). Stays true after excluding one,
     *  so the endpoint checkbox never disappears and the exclusion can be undone. */
    get editable(): boolean {
        return !!this._explain?.editable;
    }
    /** Breakdown caption ("By source" / "By endpoint"). */
    get byLabel(): string {
        return this.isSourceRows ? 'By source' : 'By endpoint';
    }

    /** The list-table reports the hovered row (or null on leave); forward its flows to the page. */
    onRowHover(row: CovTableRow | null): void {
        this.rowHover.emit(row ? { flows: row.flows } : null);
    }

    /** A node's include checkbox was toggled; emit its items (cascade for a parent) with the new intent. */
    onIncludeToggle(row: CovTableRow): void {
        if (row.sources.length) {
            this.toggleInclude.emit({
                target: this.isSourceRows ? 'source' : 'endpoint',
                items: row.sources,
                include: !row.included,
            });
        }
    }

    verdictTone(row: CoverageRow): CoverageStateTone {
        return this.toneForState(row.state);
    }
    verdictLabel(row: CoverageRow): string {
        return this.labelForTone(this.toneForState(row.state));
    }

    /** A coverage state's tone: an uncovered SOURCE at a governed endpoint reads as "excluded" (the service
     *  does not admit it); an uncovered endpoint, or anything with no governing service, is "uncovered". */
    private toneForState(state: CoverageState): CoverageStateTone {
        if (state === 'covered') return 'covered';
        if (state === 'partial') return 'partial';
        return this.isSourceRows && this.governed ? 'excluded' : 'uncovered';
    }
    private labelForTone(tone: CoverageStateTone): string {
        return tone === 'covered' ? 'Covered' : tone === 'partial' ? 'Partial' : tone === 'excluded' ? 'Excluded' : 'Uncovered';
    }

    private rebuild(): void {
        const tree = this._explain?.tree;
        this.isTree = !!tree?.length;
        this.tableOptions = this.buildTableOptions();
        this.columns = this.buildColumns();
        this.childColumnsByLevel = this.isTree ? [[], this.childColumns()] : [];
        this.rowData = this.isTree
            ? (tree ?? []).map((node, i) => this.toTreeRow(node, `${i}`))
            : (this._explain?.rows ?? []).map((row, i) => this.toFlatRow(row, i));
    }

    private toTreeRow(node: CoverageTreeNode, id: string): CovTableRow {
        const tone = this.toneForState(node.state);
        return {
            id,
            primary: node.label,
            circuits: node.circuits,
            tone,
            stateLabel: this.labelForTone(tone),
            included: node.included,
            indeterminate: node.indeterminate,
            sources: node.sources,
            flows: node.flows,
            children: node.children?.map((child, j) => this.toTreeRow(child, `${id}.${j}`)),
        };
    }

    private toFlatRow(row: CoverageRow, i: number): CovTableRow {
        const tone = this.toneForState(row.state);
        return {
            id: `${i}`,
            primary: row.label,
            circuits: row.circuits,
            service: row.service || 'none',
            tone,
            stateLabel: this.labelForTone(tone),
            included: row.state === 'covered',
            indeterminate: false,
            sources: [],
            flows: row.flows ?? [],
        };
    }

    // --- columns --------------------------------------------------------------

    /** Whether the include checkbox column shows. Source rows edit admission, so they need a service that
     *  governs the endpoint. Endpoint rows edit a service's targets, so they only need a service to exist -
     *  and must keep the checkbox after an endpoint is excluded (un-governing the view) so it can be re-added. */
    private get canInclude(): boolean {
        return this.isTree && (this.isSourceRows ? this.governed : this.editable);
    }

    private buildColumns(): ListColumn[] {
        if (!this.isTree) {
            return [this.primaryColumn('Endpoint', false), this.circuitsColumn(), this.serviceColumn(), this.stateColumn()];
        }
        const label = this.isSourceRows ? 'Source identity' : 'Host identity';
        const cols = [this.primaryColumn(label, true), this.circuitsColumn(), this.stateColumn()];
        return this.canInclude ? [this.includeColumn(), ...cols] : cols;
    }

    /** The columns under each deeper tree level (level 0 uses the main columns). */
    private childColumns(): ListColumn[] {
        const label = this.isSourceRows ? 'Source address' : 'Host address';
        const cols = [this.primaryColumn(label, true), this.circuitsColumn(), this.stateColumn()];
        return this.canInclude ? [this.includeColumn(), ...cols] : cols;
    }

    private includeColumn(): ListColumn {
        return {
            key: 'include',
            label: '',
            width: 34,
            hideable: false,
            reorderable: false,
            resizable: false,
            excludeFromChooser: true,
            cellClass: 'cov-c-include',
            cell: CoverageIncludeCellComponent,
            cellInputs: (ctx) => {
                const row = ctx.row as CovTableRow;
                return {
                    checked: row?.included ?? false,
                    indeterminate: row?.indeterminate ?? false,
                    onToggle: () => this.onIncludeToggle(row),
                };
            },
        };
    }

    private primaryColumn(label: string, treeColumn: boolean): ListColumn {
        return { key: 'primary', field: 'primary', label, treeColumn, cellClass: 'cov-c-primary', resizable: true };
    }

    private circuitsColumn(): ListColumn {
        return {
            key: 'circuits',
            field: 'circuits',
            label: 'Circ',
            width: 74,
            resizable: false,
            cellClass: 'cov-c-circ',
            valueFormatter: (shim) => (Number(shim.value) || 0).toLocaleString(),
        };
    }

    private serviceColumn(): ListColumn {
        return { key: 'service', field: 'service', label: 'Service', cellClass: 'cov-c-service' };
    }

    private stateColumn(): ListColumn {
        return {
            key: 'state',
            label: 'State',
            width: 124,
            resizable: false,
            hideable: false,
            reorderable: false,
            excludeFromChooser: true,
            cellClass: 'cov-c-state',
            cell: CoverageStateCellComponent,
            cellInputs: (ctx) => {
                const row = ctx.row as CovTableRow;
                return { tone: row?.tone ?? 'uncovered', label: row?.stateLabel ?? '' };
            },
        };
    }
}
